import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import interiorCatalogApi from "../services/interiorCatalogApi";

import MountingSketchCanvas2D from "../components/mounting-editor/MountingSketchCanvas2D";
import MountingPreview3D from "../components/mounting-editor/MountingPreview3D";
import MountingElementInspector from "../components/mounting-editor/MountingElementInspector";
import MountingDrillingsPanel from "../components/mounting-editor/MountingDrillingsPanel";
import MountingItemsPanel from "../components/mounting-editor/MountingItemsPanel";

function normalizeAssetMounting(item, assetId) {
  return {
    id: item?.id || null,
    asset_id: item?.asset_id || assetId,
    mount_type: item?.mount_type || "board_face_mount",

    reference_frame_json: item?.reference_frame_json || {
      origin: { x: 0, y: 0, z: 0 },
      defaultRotation: { x: 0, y: 0, z: 0 },
      leftRotation: { x: 0, y: 0, z: 0 },
      rightRotation: { x: 0, y: 180, z: 0 },
      offset: { x: 0, y: 0, z: 0 },
    },

    placement_rules_json: item?.placement_rules_json || {
      hardwareKind: "generic",
      handedness: "universal",
      mirrorAllowed: true,
      workPlane: {
        plane: "xy",
        offset: 0,
        lockDrillAxis: true,
      },
      attach_points: [],
      components: [],
    },

    drilling_pattern_json: item?.drilling_pattern_json || {
      holes: [],
      drillings: [],
    },

    processing_json: item?.processing_json || {
      cutouts: [],
    },

    status: item?.status || "active",
  };
}

function mountingToEditorModel(mounting) {
  return {
    origin: mounting.reference_frame_json?.origin || { x: 0, y: 0, z: 0 },

    referenceDimensions:
  mounting.placement_rules_json?.reference_dimensions || [],

    defaultRotation:
      mounting.reference_frame_json?.defaultRotation || { x: 0, y: 0, z: 0 },

    leftRotation:
      mounting.reference_frame_json?.leftRotation || { x: 0, y: 0, z: 0 },

    rightRotation:
      mounting.reference_frame_json?.rightRotation || { x: 0, y: 180, z: 0 },

    offset:
      mounting.reference_frame_json?.offset || { x: 0, y: 0, z: 0 },

    hardwareKind:
      mounting.placement_rules_json?.hardwareKind || "generic",

    handedness:
      mounting.placement_rules_json?.handedness || "universal",

    mirrorAllowed:
      mounting.placement_rules_json?.mirrorAllowed !== false,

      workPlane: mounting.placement_rules_json?.workPlane || {
  plane: "xy",
  offset: 0,
  lockDrillAxis: true,
},

    components:
      mounting.placement_rules_json?.components || [],

    attachPoints: mounting.placement_rules_json?.attach_points || [],

holes: mounting.drilling_pattern_json?.holes || [],

drillings:
  mounting.drilling_pattern_json?.drillings ||
  mounting.drillings ||
  [],

cutouts: mounting.processing_json?.cutouts || [],
  };
}

function editorModelToMounting(mounting, model) {
  return {
    ...mounting,

    reference_frame_json: {
      ...(mounting.reference_frame_json || {}),
      origin: model.origin || { x: 0, y: 0, z: 0 },
      defaultRotation: model.defaultRotation || { x: 0, y: 0, z: 0 },
      leftRotation: model.leftRotation || { x: 0, y: 0, z: 0 },
      rightRotation: model.rightRotation || { x: 0, y: 180, z: 0 },
      offset: model.offset || { x: 0, y: 0, z: 0 },
    },

    placement_rules_json: {
      ...(mounting.placement_rules_json || {}),
      hardwareKind: model.hardwareKind || "generic",
      handedness: model.handedness || "universal",
      mirrorAllowed: model.mirrorAllowed !== false,
      components: model.components || [],
      workPlane: model.workPlane || {
  plane: "xy",
  offset: 0,
  lockDrillAxis: true,
},
      attach_points: model.attachPoints || [],
      reference_dimensions: model.referenceDimensions || [],
    },

    drilling_pattern_json: {
  ...(mounting.drilling_pattern_json || {}),
  holes: model.holes || [],
  drillings: model.drillings || [],
},

    processing_json: {
      ...(mounting.processing_json || {}),
      cutouts: model.cutouts || [],
    },
  };
}

function TabButton({ active, children, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-2 text-sm border-b-2 whitespace-nowrap ${
        active
          ? "border-blue-600 text-blue-700 bg-blue-50 font-medium"
          : "border-transparent text-slate-500 hover:text-slate-800"
      }`}
    >
      {children}
    </button>
  );
}

function HeaderButton({ children, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="px-3 py-1.5 rounded border bg-white text-sm hover:bg-slate-50"
    >
      {children}
    </button>
  );
}

function ReadOnlyField({ label, value }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-500 mb-1">
        {label}
      </label>
      <input
        value={value || ""}
        readOnly
        className="w-full px-3 py-2 border rounded bg-slate-50 text-sm"
      />
    </div>
  );
}

function getAssetGlbPath(asset) {
  return (
    asset?.converted_file_path ||
    asset?.convertedFilePath ||
    asset?.converted_file ||
    asset?.convertedFile ||
    asset?.file_url ||
    ""
  );
}

function resolveItemAsset(foundItem, assets = []) {
  if (!foundItem) return null;

  if (foundItem.linked_asset) {
    return foundItem.linked_asset;
  }

  if (foundItem.linkedAsset) {
    return foundItem.linkedAsset;
  }

  const assetId =
    foundItem.asset_id ||
    foundItem.assetId ||
    foundItem.linked_asset_id ||
    foundItem.linkedAssetId ||
    null;

  if (assetId) {
    const found = assets.find((x) => String(x.id) === String(assetId));

    if (found) return found;

    return {
      id: assetId,
      code: foundItem.asset_code || foundItem.assetCode || "",
      name: foundItem.asset_name || foundItem.assetName || "",
      asset_type: foundItem.asset_type || foundItem.assetType || "",
      source_file_path: foundItem.source_file_path || foundItem.sourceFilePath || "",
      converted_file_path:
        foundItem.converted_file_path ||
        foundItem.convertedFilePath ||
        foundItem.converted_file ||
        foundItem.convertedFile ||
        "",
      thumbnail_file_path:
        foundItem.thumbnail_file_path || foundItem.thumbnailFilePath || "",
      conversion_status:
        foundItem.conversion_status || foundItem.conversionStatus || "",
    };
  }

  if (foundItem.converted_file_path || foundItem.convertedFilePath) {
    return {
      id: null,
      converted_file_path:
        foundItem.converted_file_path || foundItem.convertedFilePath,
    };
  }

  return null;
}

export default function InteriorAssetMountingEditorPage() {
  const navigate = useNavigate();
  const { assetId } = useParams();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [asset, setAsset] = useState(null);
  const [mounting, setMounting] = useState(null);
  const [editorModel, setEditorModel] = useState(null);

  const [selectedElement, setSelectedElement] = useState(null);
  const [activeView, setActiveView] = useState("xy");
  const [activeInspectorTab, setActiveInspectorTab] = useState("item");
  const [errorText, setErrorText] = useState("");
  const [selectedDrillId, setSelectedDrillId] = useState(null);

  const [mountingItems, setMountingItems] = useState([]);
const [availableItems, setAvailableItems] = useState([]);
const [showOrigins, setShowOrigins] = useState(true);

const [toolMode, setToolMode] = useState("select");
const [pickedPoints, setPickedPoints] = useState([]);
const [selectedPickPoint, setSelectedPickPoint] = useState(null);
const [selectedDimensionId, setSelectedDimensionId] = useState(null);
const [markerSize, setMarkerSize] = useState(0.5);
const [dimensionOffset, setDimensionOffset] = useState(10);
const [dimensionMode, setDimensionMode] = useState("free");

const dimension = useMemo(() => {
  if (pickedPoints.length < 2) return null;

  const a = pickedPoints[0].local;
  const b = pickedPoints[1].local;

  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const dz = b.z - a.z;

  const free = Math.sqrt(dx * dx + dy * dy + dz * dz);

  const valueByMode = {
    free,
    x: Math.abs(dx),
    y: Math.abs(dy),
    z: Math.abs(dz),
  };

  return {
    mode: dimensionMode,
    hasPlacementPoint: pickedPoints.length >= 3,
    value: Number((valueByMode[dimensionMode] || free).toFixed(3)),
    dx: Number(dx.toFixed(3)),
    dy: Number(dy.toFixed(3)),
    dz: Number(dz.toFixed(3)),
    free: Number(free.toFixed(3)),
  };
}, [pickedPoints, dimensionMode]);

const selectedDimension = useMemo(() => {
  if (!selectedDimensionId) return null;

  return (editorModel?.referenceDimensions || []).find(
    (dimension) => String(dimension.id) === String(selectedDimensionId)
  );
}, [editorModel?.referenceDimensions, selectedDimensionId]);

function updateSelectedDimension(patch) {
  if (!selectedDimensionId) return;

  setEditorModel((prev) => ({
    ...prev,
    referenceDimensions: (prev.referenceDimensions || []).map((dimension) =>
      String(dimension.id) === String(selectedDimensionId)
        ? {
            ...dimension,
            ...patch,
            updatedAt: new Date().toISOString(),
          }
        : dimension
    ),
  }));
}

function resetSelectedDimensionOffset() {
  if (!selectedDimensionId) return;

  setEditorModel((prev) => ({
    ...prev,
    referenceDimensions: (prev.referenceDimensions || []).map((dimension) => {
      if (String(dimension.id) !== String(selectedDimensionId)) {
        return dimension;
      }

      const next = { ...dimension };
      delete next.offsetDistance;
      next.updatedAt = new Date().toISOString();

      return next;
    }),
  }));
}

function handlePickPoint(point) {
  setSelectedPickPoint(point);

  if (toolMode === "measure") {
    setPickedPoints((prev) => {
      if (prev.length === 0) {
        return [point];
      }

      if (prev.length === 1) {
        return [prev[0], point];
      }

      if (prev.length === 2) {
        if (point.type !== "dimension_placement") {
          return prev;
        }

        const newDimension = {
          id: `dim_${Date.now()}`,
          mode: dimensionMode,
          points: [prev[0], prev[1], point],
          label: "",
          offsetDistance: null,
          createdAt: new Date().toISOString(),
        };

        setEditorModel((current) => ({
          ...current,
          referenceDimensions: [
            ...(current.referenceDimensions || []),
            newDimension,
          ],
        }));

        setSelectedDimensionId(newDimension.id);

        return [];
      }

      return [point];
    });

    return;
  }

  setPickedPoints([point]);
  setSelectedDimensionId(null);
}

function deleteSelectedDimension() {
  if (!selectedDimensionId) return;

  setEditorModel((prev) => ({
    ...prev,
    referenceDimensions: (prev.referenceDimensions || []).filter(
      (dimension) => String(dimension.id) !== String(selectedDimensionId)
    ),
  }));

  setSelectedDimensionId(null);
}

function alignPickedPointToOriginAxes(axes = ["x", "y", "z"]) {
  if (!selectedPickPoint?.local) return;

  setEditorModel((prev) => {
    const currentOffset = prev.offset || { x: 0, y: 0, z: 0 };

    const nextOffset = {
      x: Number(currentOffset.x || 0),
      y: Number(currentOffset.y || 0),
      z: Number(currentOffset.z || 0),
    };

    axes.forEach((axis) => {
      nextOffset[axis] = Number((-selectedPickPoint.local[axis]).toFixed(3));
    });

    return {
      ...prev,
      offset: nextOffset,
    };
  });
}

function alignPickedPointToWorkPlane() {
  if (!selectedPickPoint?.local) return;

  const plane = editorModel?.workPlane?.plane || "xy";
  const planeOffset = Number(editorModel?.workPlane?.offset || 0);

  let axis = "z";

  if (plane === "xy") axis = "z";
  if (plane === "xz") axis = "y";
  if (plane === "yz") axis = "x";

  setEditorModel((prev) => {
    const currentOffset = prev.offset || { x: 0, y: 0, z: 0 };

    return {
      ...prev,
      offset: {
        ...currentOffset,
        [axis]: Number((planeOffset - selectedPickPoint.local[axis]).toFixed(3)),
      },
    };
  });
}

function clearPickedPoints() {
  setPickedPoints([]);
  setSelectedPickPoint(null);
}

async function load() {
  try {
    setLoading(true);
    setErrorText("");

    const [assetRes, mountingRes] = await Promise.all([
      interiorCatalogApi.getAssetById(assetId),
      interiorCatalogApi.getAssetMounting(assetId),
    ]);

    const foundAsset =
      assetRes?.item ||
      assetRes?.asset ||
      assetRes?.data ||
      assetRes ||
      null;

    if (!foundAsset) {
      throw new Error("Asset not found.");
    }

    const foundMounting =
      mountingRes?.item ||
      mountingRes?.mounting ||
      null;

    const normalized = normalizeAssetMounting(foundMounting, foundAsset.id);

let mountingItemsRes = { items: [] };

if (normalized.id) {
  mountingItemsRes = await interiorCatalogApi.listAssetMountingItems(
    normalized.id
  );
}

const itemsRes = await interiorCatalogApi.listFamilyItems({
  status: "active",
  limit: 200,
  offset: 0,
});

setAsset(foundAsset);
setMounting(normalized);
setEditorModel(mountingToEditorModel(normalized));
setMountingItems(mountingItemsRes?.items || []);
setAvailableItems(itemsRes?.items || []);
setSelectedElement(null);
setSelectedDrillId(null);
  } catch (err) {
    console.error("load asset mounting editor error:", err);
    setErrorText(err?.message || "Failed to load asset mounting.");
  } finally {
    setLoading(false);
  }
}

  useEffect(() => {
  load();
}, [assetId]);

  const currentPlane = useMemo(() => {
    if (activeView === "xy") return "xy";
    if (activeView === "xz") return "xz";
    return "yz";
  }, [activeView]);

  const glbUrl = useMemo(() => {
  const path = getAssetGlbPath(asset);

  if (!path) return "";

  const str = String(path);

  if (str.startsWith("http")) return str;
  if (str.startsWith("/")) return `https://modularobjects.com${str}`;

  return `https://modularobjects.com/${str}`;
}, [asset]);

  async function handleSave() {
  if (!mounting || !editorModel) return;

  try {
    setSaving(true);

    const payload = editorModelToMounting(mounting, editorModel);

    if (mounting.id) {
      const res = await interiorCatalogApi.updateAssetMounting(
        mounting.id,
        payload
      );

      const next = normalizeAssetMounting(res.item, assetId);
      setMounting(next);
      setEditorModel(mountingToEditorModel(next));
    } else {
      const res = await interiorCatalogApi.createAssetMounting(
        assetId,
        payload
      );

      const next = normalizeAssetMounting(res.item, assetId);
      setMounting(next);
      setEditorModel(mountingToEditorModel(next));
    }
  } catch (err) {
    console.error("save asset mounting error:", err);
    alert(err?.response?.data?.message || err?.message || "Save failed.");
  } finally {
    setSaving(false);
  }
}

  if (loading) {
    return (
      <div className="h-[calc(100vh-80px)] flex items-center justify-center text-slate-500">
        Loading mounting editor...
      </div>
    );
  }

  if (!asset || !mounting || !editorModel) {
  return (
    <div className="h-[calc(100vh-80px)] flex items-center justify-center text-red-600">
      {errorText || "Missing asset or mounting."}
    </div>
  );
}

  return (
    <div className="h-[calc(100vh-80px)] overflow-hidden flex flex-col bg-slate-100">
      <div className="h-14 shrink-0 border-b bg-white px-3 flex items-center justify-between">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-wide text-slate-500">
            Asset Mounting Editor
          </div>
          <div className="text-sm font-semibold truncate text-slate-800">
  {asset?.name || asset?.code || "Asset"}
</div>
<div className="text-xs text-slate-500 truncate">
  asset #{asset?.id} · {asset?.code}
</div>
        </div>

        <div className="flex gap-2">
          <HeaderButton onClick={() => navigate("/interior-editor/catalog")}>
            Catalog
          </HeaderButton>
          <HeaderButton onClick={() => navigate("/interior-editor/assets")}>
            Assets
          </HeaderButton>
          <HeaderButton onClick={() => navigate(`/interior-editor/assets?assetId=${asset?.id}`)}>
  Asset Details
</HeaderButton>
          <HeaderButton onClick={() => navigate(-1)}>Back</HeaderButton>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-1.5 rounded bg-green-600 text-white text-sm disabled:opacity-50"
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>

      {errorText ? (
        <div className="shrink-0 border-b bg-red-50 text-red-700 px-3 py-2 text-sm">
          {errorText}
        </div>
      ) : null}

      <div className="flex-1 min-h-0 overflow-hidden grid grid-cols-[340px_minmax(0,1fr)]">
        <aside className="min-h-0 border-r bg-white flex flex-col overflow-hidden">
          <div className="shrink-0 border-b px-3 pt-3 bg-white">
            <div className="font-semibold text-sm text-slate-800">
              Mounting Manager
            </div>
            <div className="text-xs text-slate-500 mb-2">
              Attach points, drilling pattern and processing rules.
            </div>

            <div className="flex gap-1 overflow-x-auto">
              <TabButton
  active={activeInspectorTab === "item"}
  onClick={() => setActiveInspectorTab("item")}
>
  Asset
</TabButton>
              <TabButton
                active={activeInspectorTab === "mounting"}
                onClick={() => setActiveInspectorTab("mounting")}
              >
                Mounting
              </TabButton>
              <TabButton
  active={activeInspectorTab === "hardware"}
  onClick={() => setActiveInspectorTab("hardware")}
>
  Hardware
</TabButton>
<TabButton
  active={activeInspectorTab === "tools"}
  onClick={() => setActiveInspectorTab("tools")}
>
  Tools
</TabButton>
              <TabButton
                active={activeInspectorTab === "element"}
                onClick={() => setActiveInspectorTab("element")}
              >
                Selected
              </TabButton>
              <TabButton
  active={activeInspectorTab === "drillings"}
  onClick={() => setActiveInspectorTab("drillings")}
>
  Drillings
</TabButton>
              <TabButton
                active={activeInspectorTab === "debug"}
                onClick={() => setActiveInspectorTab("debug")}
              >
                Debug
              </TabButton>
            </div>
          </div>

          <div className="flex-1 min-h-0 overflow-auto p-3">
            {activeInspectorTab === "item" && (
              <div className="space-y-3">
                <div className="border rounded p-3 bg-slate-50">
  <div className="font-semibold text-sm">{asset.name}</div>
  <div className="text-xs text-slate-500">{asset.code}</div>
</div>

<ReadOnlyField label="Asset ID" value={asset?.id || ""} />
<ReadOnlyField label="Asset type" value={asset?.asset_type || ""} />
<ReadOnlyField label="Source" value={asset?.source_file_path || ""} />
<ReadOnlyField label="GLB" value={getAssetGlbPath(asset)} />
<ReadOnlyField label="Resolved GLB URL" value={glbUrl || ""} />
              </div>
            )}

            {activeInspectorTab === "mounting" && (
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">
                    Mount Type
                  </label>
                  <input
                    value={mounting.mount_type || ""}
                    onChange={(e) =>
                      setMounting((prev) => ({
                        ...prev,
                        mount_type: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border rounded"
                  />
                </div>

                <div className="border rounded p-3 bg-white space-y-3">
  <div>
    <div className="font-semibold text-sm">Hardware mounting metadata</div>
    <div className="text-xs text-slate-500">
      Defines how this GLB behaves when attached to a panel.
    </div>
  </div>

  <div>
    <label className="block text-xs font-medium text-slate-500 mb-1">
      Hardware kind
    </label>
    <select
      value={editorModel.hardwareKind || "generic"}
      onChange={(e) =>
        setEditorModel((prev) => ({
          ...prev,
          hardwareKind: e.target.value,
        }))
      }
      className="w-full px-3 py-2 border rounded bg-white"
    >
      <option value="generic">generic</option>
      <option value="hinge">hinge</option>
      <option value="handle">handle</option>
      <option value="lift">lift</option>
      <option value="runner">runner</option>
      <option value="connector">connector</option>
      <option value="support">support</option>
    </select>
  </div>

  <div>
    <label className="block text-xs font-medium text-slate-500 mb-1">
      Handedness
    </label>
    <select
      value={editorModel.handedness || "universal"}
      onChange={(e) =>
        setEditorModel((prev) => ({
          ...prev,
          handedness: e.target.value,
        }))
      }
      className="w-full px-3 py-2 border rounded bg-white"
    >
      <option value="universal">universal - same model rotates/mirrors</option>
      <option value="left">left component</option>
      <option value="right">right component</option>
      <option value="pair">pair - left and right components</option>
      <option value="set">set / assembly</option>
    </select>
  </div>

  <label className="flex items-center gap-2 text-sm">
    <input
      type="checkbox"
      checked={editorModel.mirrorAllowed !== false}
      onChange={(e) =>
        setEditorModel((prev) => ({
          ...prev,
          mirrorAllowed: e.target.checked,
        }))
      }
    />
    Mirror / rotate allowed
  </label>
</div>

<div className="border rounded p-3 bg-white space-y-3">
  <div>
    <div className="font-semibold text-sm">Reference frame correction</div>
    <div className="text-xs text-slate-500">
      Local offset and rotations used when placing the GLB on a panel.
    </div>
  </div>

  <div className="border rounded p-3 bg-white space-y-3">
  <div>
    <div className="font-semibold text-sm">Drilling work plane</div>
    <div className="text-xs text-slate-500">
      Fixed plane used for placing drilling points in 3D.
    </div>
  </div>

  <div>
    <label className="block text-xs font-medium text-slate-500 mb-1">
      Work plane
    </label>
    <select
      value={editorModel.workPlane?.plane || "xy"}
      onChange={(e) =>
        setEditorModel((prev) => ({
          ...prev,
          workPlane: {
            ...(prev.workPlane || {}),
            plane: e.target.value,
          },
        }))
      }
      className="w-full px-3 py-2 border rounded bg-white"
    >
      <option value="xy">XY - front/back face</option>
      <option value="xz">XZ - horizontal/top view</option>
      <option value="yz">YZ - side face</option>
    </select>
  </div>

  <div>
    <label className="block text-xs font-medium text-slate-500 mb-1">
      Plane offset
    </label>
    <input
      type="number"
      value={editorModel.workPlane?.offset ?? 0}
      onChange={(e) =>
        setEditorModel((prev) => ({
          ...prev,
          workPlane: {
            ...(prev.workPlane || {}),
            offset: Number(e.target.value),
          },
        }))
      }
      className="w-full px-3 py-2 border rounded"
    />
  </div>

  <label className="flex items-center gap-2 text-sm">
    <input
      type="checkbox"
      checked={editorModel.workPlane?.lockDrillAxis !== false}
      onChange={(e) =>
        setEditorModel((prev) => ({
          ...prev,
          workPlane: {
            ...(prev.workPlane || {}),
            lockDrillAxis: e.target.checked,
          },
        }))
      }
    />
    Lock drilling axis perpendicular to work plane
  </label>
</div>

  <div className="grid grid-cols-3 gap-2">
    {["x", "y", "z"].map((axis) => (
      <div key={`offset_${axis}`}>
        <label className="block text-xs font-medium text-slate-500 mb-1">
          Offset {axis.toUpperCase()}
        </label>
        <input
          type="number"
          value={editorModel.offset?.[axis] ?? 0}
          onChange={(e) =>
            setEditorModel((prev) => ({
              ...prev,
              offset: {
                ...(prev.offset || {}),
                [axis]: Number(e.target.value),
              },
            }))
          }
          className="w-full px-3 py-2 border rounded"
        />
      </div>
    ))}
  </div>

  <div className="grid grid-cols-3 gap-2">
    {["x", "y", "z"].map((axis) => (
      <div key={`default_rot_${axis}`}>
        <label className="block text-xs font-medium text-slate-500 mb-1">
          Default rot {axis.toUpperCase()}
        </label>
        <input
          type="number"
          value={editorModel.defaultRotation?.[axis] ?? 0}
          onChange={(e) =>
            setEditorModel((prev) => ({
              ...prev,
              defaultRotation: {
                ...(prev.defaultRotation || {}),
                [axis]: Number(e.target.value),
              },
            }))
          }
          className="w-full px-3 py-2 border rounded"
        />
      </div>
    ))}
  </div>

  <div className="grid grid-cols-3 gap-2">
    {["x", "y", "z"].map((axis) => (
      <div key={`left_rot_${axis}`}>
        <label className="block text-xs font-medium text-slate-500 mb-1">
          Left rot {axis.toUpperCase()}
        </label>
        <input
          type="number"
          value={editorModel.leftRotation?.[axis] ?? 0}
          onChange={(e) =>
            setEditorModel((prev) => ({
              ...prev,
              leftRotation: {
                ...(prev.leftRotation || {}),
                [axis]: Number(e.target.value),
              },
            }))
          }
          className="w-full px-3 py-2 border rounded"
        />
      </div>
    ))}
  </div>

  <div className="grid grid-cols-3 gap-2">
    {["x", "y", "z"].map((axis) => (
      <div key={`right_rot_${axis}`}>
        <label className="block text-xs font-medium text-slate-500 mb-1">
          Right rot {axis.toUpperCase()}
        </label>
        <input
          type="number"
          value={editorModel.rightRotation?.[axis] ?? 0}
          onChange={(e) =>
            setEditorModel((prev) => ({
              ...prev,
              rightRotation: {
                ...(prev.rightRotation || {}),
                [axis]: Number(e.target.value),
              },
            }))
          }
          className="w-full px-3 py-2 border rounded"
        />
      </div>
    ))}
  </div>
</div>

                <div className="border rounded p-3 bg-slate-50 text-xs text-slate-600">
                  Attach points define where this item can connect to a board or
                  cabinet. Holes and cutouts are used later for CNC/processing.
                </div>
              </div>
            )}

            {activeInspectorTab === "hardware" && (
  <MountingItemsPanel
    mounting={mounting}
    mountingItems={mountingItems}
    setMountingItems={setMountingItems}
    availableItems={availableItems}
    drillings={editorModel?.drillings || []}
    mountingScope="asset_mounting"
  />
)}

            {activeInspectorTab === "element" && (
              <MountingElementInspector
                model={editorModel}
                selectedElement={selectedElement}
                onChangeModel={setEditorModel}
              />
            )}

            {activeInspectorTab === "drillings" && (
  <MountingDrillingsPanel
    mounting={editorModel}
    setMounting={setEditorModel}
    selectedDrillId={selectedDrillId}
    setSelectedDrillId={setSelectedDrillId}
  />
)}

{activeInspectorTab === "tools" && (
  <div className="space-y-3">
    <div className="border rounded p-3 bg-white space-y-3">
      <div>
        <div className="font-semibold text-sm">GLB precision tools</div>
        <div className="text-xs text-slate-500">
          Pick points on GLB, measure distances and align mounting offset.
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-slate-500 mb-1">
          Tool mode
        </label>
        <select
          value={toolMode}
          onChange={(e) => {
            setToolMode(e.target.value);
            clearPickedPoints();
          }}
          className="w-full px-3 py-2 border rounded bg-white"
        >
          <option value="select">Select</option>
          <option value="pick_point">Pick point on GLB</option>
          <option value="measure">Measure / Dimension</option>
        </select>
      </div>

      <div>
        <label className="block text-xs font-medium text-slate-500 mb-1">
          Dimension type
        </label>
        <select
          value={dimensionMode}
          onChange={(e) => setDimensionMode(e.target.value)}
          className="w-full px-3 py-2 border rounded bg-white"
        >
          <option value="free">Free 3D distance</option>
          <option value="x">X axis distance</option>
          <option value="y">Y axis distance</option>
          <option value="z">Z axis distance</option>
        </select>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">
            Marker size
          </label>
          <input
            type="number"
            step="0.1"
            min="0.1"
            value={markerSize}
            onChange={(e) => setMarkerSize(Number(e.target.value) || 0.5)}
            className="w-full px-3 py-2 border rounded"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">
            Default dim offset
          </label>
          <input
            type="number"
            step="1"
            value={dimensionOffset}
            onChange={(e) => setDimensionOffset(Number(e.target.value) || 10)}
            className="w-full px-3 py-2 border rounded"
          />
        </div>
      </div>

      {toolMode === "measure" ? (
        <div className="text-xs text-slate-500 border rounded p-2 bg-slate-50">
          Click 1: start point · Click 2: end point · Click 3: dimension line / text position.
        </div>
      ) : null}

      <button
        type="button"
        onClick={clearPickedPoints}
        className="w-full px-3 py-2 border rounded bg-white text-sm hover:bg-slate-50"
      >
        Clear picked points
      </button>
    </div>

    {selectedPickPoint ? (
      <div className="border rounded p-3 bg-slate-50 text-xs space-y-3">
        <div className="font-semibold text-slate-700">Picked point</div>

        <div className="grid grid-cols-2 gap-y-1">
          <div className="text-slate-500">Local X</div>
          <div>{selectedPickPoint.local.x}</div>

          <div className="text-slate-500">Local Y</div>
          <div>{selectedPickPoint.local.y}</div>

          <div className="text-slate-500">Local Z</div>
          <div>{selectedPickPoint.local.z}</div>

          <div className="text-slate-500">World X</div>
          <div>{selectedPickPoint.world.x}</div>

          <div className="text-slate-500">World Y</div>
          <div>{selectedPickPoint.world.y}</div>

          <div className="text-slate-500">World Z</div>
          <div>{selectedPickPoint.world.z}</div>
        </div>

        <div className="space-y-2">
          <div className="text-xs font-semibold text-slate-600">
            Align picked point to mounting origin
          </div>

          <div className="grid grid-cols-3 gap-2">
            <button type="button" onClick={() => alignPickedPointToOriginAxes(["x"])} className="px-2 py-1.5 border rounded bg-white text-xs hover:bg-blue-50 text-blue-700">X</button>
            <button type="button" onClick={() => alignPickedPointToOriginAxes(["y"])} className="px-2 py-1.5 border rounded bg-white text-xs hover:bg-blue-50 text-blue-700">Y</button>
            <button type="button" onClick={() => alignPickedPointToOriginAxes(["z"])} className="px-2 py-1.5 border rounded bg-white text-xs hover:bg-blue-50 text-blue-700">Z</button>
          </div>

          <div className="grid grid-cols-4 gap-2">
            <button type="button" onClick={() => alignPickedPointToOriginAxes(["x", "y"])} className="px-2 py-1.5 border rounded bg-white text-xs hover:bg-blue-50 text-blue-700">XY</button>
            <button type="button" onClick={() => alignPickedPointToOriginAxes(["x", "z"])} className="px-2 py-1.5 border rounded bg-white text-xs hover:bg-blue-50 text-blue-700">XZ</button>
            <button type="button" onClick={() => alignPickedPointToOriginAxes(["y", "z"])} className="px-2 py-1.5 border rounded bg-white text-xs hover:bg-blue-50 text-blue-700">YZ</button>
            <button type="button" onClick={() => alignPickedPointToOriginAxes(["x", "y", "z"])} className="px-2 py-1.5 border rounded bg-blue-600 text-xs text-white hover:bg-blue-700">XYZ</button>
          </div>
        </div>

        <button
          type="button"
          onClick={alignPickedPointToWorkPlane}
          className="w-full px-3 py-2 border rounded bg-white text-sm hover:bg-blue-50 text-blue-700"
        >
          Align picked point to work plane
        </button>
      </div>
    ) : (
      <div className="text-sm text-slate-400 border rounded p-3 bg-slate-50">
        No point selected. Switch to Pick point or Measure and click on the GLB.
      </div>
    )}

    {dimension ? (
      <div className="border rounded p-3 bg-blue-50 text-xs space-y-1">
        <div className="font-semibold text-blue-800">Dimension</div>
        <div>Type: {dimension.mode === "free" ? "Free 3D" : dimension.mode.toUpperCase()}</div>
        <div className="text-lg font-semibold text-blue-900">{dimension.value} mm</div>
        <div className="border-t pt-2 mt-2 text-slate-600">
          <div>Free: {dimension.free} mm</div>
          <div>DX: {dimension.dx} mm</div>
          <div>DY: {dimension.dy} mm</div>
          <div>DZ: {dimension.dz} mm</div>
        </div>
      </div>
    ) : null}

    <div className="border rounded p-3 bg-white text-xs space-y-1">
      <div className="font-semibold text-slate-700">Current mounting offset</div>
      <div>X: {Number(editorModel?.offset?.x || 0).toFixed(3)}</div>
      <div>Y: {Number(editorModel?.offset?.y || 0).toFixed(3)}</div>
      <div>Z: {Number(editorModel?.offset?.z || 0).toFixed(3)}</div>
    </div>

    {selectedDimension ? (
      <div className="border rounded p-3 bg-white text-xs space-y-3">
        <div>
          <div className="font-semibold text-slate-700">
            Selected dimension
          </div>
          <div className="text-slate-500 break-all">
            {selectedDimension.id}
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">
            Dimension type
          </label>
          <select
            value={selectedDimension.mode || "free"}
            onChange={(e) =>
              updateSelectedDimension({
                mode: e.target.value,
              })
            }
            className="w-full px-3 py-2 border rounded bg-white"
          >
            <option value="free">Free 3D distance</option>
            <option value="x">X axis distance</option>
            <option value="y">Y axis distance</option>
            <option value="z">Z axis distance</option>
          </select>
        </div>

        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">
            Offset distance
          </label>
          <input
            type="number"
            step="1"
            value={selectedDimension.offsetDistance ?? ""}
            placeholder="auto / third click"
            onChange={(e) =>
              updateSelectedDimension({
                offsetDistance:
                  e.target.value === "" ? null : Number(e.target.value),
              })
            }
            className="w-full px-3 py-2 border rounded"
          />
        </div>

        <button
          type="button"
          onClick={deleteSelectedDimension}
          className="w-full px-3 py-2 border rounded bg-white text-sm text-red-600 hover:bg-red-50"
        >
          Delete selected dimension
        </button>
      </div>
    ) : null}
  </div>
)}

            {activeInspectorTab === "debug" && (
              <pre className="text-xs bg-slate-100 p-3 rounded overflow-auto">
                {JSON.stringify({ asset, mounting, editorModel }, null, 2)}
              </pre>
            )}
          </div>
        </aside>

        <main className="min-h-0 overflow-hidden flex flex-col bg-slate-100">
          <div className="h-12 shrink-0 border-b bg-white px-3 flex items-center justify-between">
            <div className="text-sm text-slate-600">
              {activeView === "preview"
                ? "3D mounting preview"
                : `2D ${currentPlane.toUpperCase()} mounting sketch`}
            </div>

            <label className="flex items-center gap-2 px-3 py-1.5 border rounded bg-white text-sm">
  <input
    type="checkbox"
    checked={showOrigins}
    onChange={(e) => setShowOrigins(e.target.checked)}
  />
  Origins
</label>

            <div className="flex gap-2">
              {["xy", "xz", "yz", "preview"].map((view) => (
                <button
                  key={view}
                  type="button"
                  onClick={() => setActiveView(view)}
                  className={`px-3 py-1.5 rounded border text-sm ${
                    activeView === view ? "bg-blue-600 text-white" : "bg-white"
                  }`}
                >
                  {view === "preview" ? "3D Preview" : `2D ${view.toUpperCase()}`}
                </button>
              ))}
            </div>
          </div>

          <div className="flex-1 min-h-0 overflow-hidden">
            {activeView === "preview" ? (
              <div className="p-3 h-full overflow-hidden">
                <MountingPreview3D
  mountingModel={editorModel}
  glbUrl={glbUrl}
  mountingItems={mountingItems}
  selectedDrillId={selectedDrillId}
  showOrigins={showOrigins}
  toolMode={toolMode}
  pickedPoints={pickedPoints}
  savedDimensions={editorModel.referenceDimensions || []}
  selectedDimensionId={selectedDimensionId}
  dimensionMode={dimensionMode}
  markerSize={markerSize}
  dimensionOffset={dimensionOffset}
  onPickPoint={handlePickPoint}
  onSelectDimension={(id) => {
    setSelectedDimensionId(id);
    setActiveInspectorTab("tools");
  }}
  onSelectDrill={(id) => {
    setSelectedDrillId(id);
    setActiveInspectorTab("drillings");
  }}
/>
              </div>
            ) : (
              <MountingSketchCanvas2D
  plane={currentPlane}
  model={editorModel}
  showOrigins={showOrigins}
  onChangeModel={setEditorModel}
  selectedElement={selectedElement}
  onSelectElement={(element) => {
    setSelectedElement(element);
    setActiveInspectorTab("element");
  }}
  selectedDrillId={selectedDrillId}
  onSelectDrill={(id) => {
    setSelectedDrillId(id);
    setSelectedElement(id ? { type: "drilling", id } : null);
    setActiveInspectorTab("drillings");
  }}
/>
            )}
          </div>
        </main>
      </div>

      <div className="h-6 shrink-0 border-t bg-white px-3 flex items-center justify-between text-[11px] text-slate-500">
        <div>
          Plane: {activeView === "preview" ? "3D" : currentPlane.toUpperCase()}
        </div>
        <div>
          Selected: {selectedElement?.type || selectedElement?.id || "none"}
        </div>
      </div>
    </div>
  );
}